import Layout from "../utilities/Layout";
import Grid from "@mui/material/Grid";
import Toolbar from "@mui/material/Toolbar";
import {useEffect, useState} from "react";
import Button from "@mui/material/Button";
import PickBracketContainerProMod from "../components/bracket/picks/two_wide/PickBracketContainerProMod";
import PickBracketContainerFourWideProMod from "../components/bracket/picks/four_wide/PickBracketContainerFourWideProMod";
import Box from "@mui/material/Box";
import {Event} from "../data/models/bracket_api/Event";
import {Pick} from "../data/models/bracket_api/Pick";
import {FourWidePick} from "../data/models/bracket_api/FourWidePick";
import {FourWideResult} from "../data/models/bracket_api/FourWideResult";
import {Bracket} from "../data/models/bracket_api/Bracket";
import {Result} from "../data/models/bracket_api/Result";
import {Qualifier} from "../data/models/bracket_api/Qualifier";
import {bracket_screen_load_pro_mod} from "../apis/ScreenLoad";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PublishIcon from '@mui/icons-material/Publish';
import {RaceClass, raceClassFromInt} from "../data/models/bracket_api/RaceClass";
import {Driver} from "../data/models/bracket_api/Driver";
import {generateBlankPicks,} from "../utilities/bracket_tools/PickGeneration";
import {User} from "../data/models/user_auth/User";
import {PickStatus} from "../data/models/bracket_api/PickStatus";
import Typography from "@mui/material/Typography";
import {League} from "../data/models/user_auth/League";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Theme,
    useMediaQuery,
    TextField,
    Autocomplete
} from "@mui/material";
import {get_drivers} from "../apis/Driver";
import CssBaseline from "@mui/material/CssBaseline";
import {Helmet} from "react-helmet";
import Loader from "../components/common/Loader";
import {GetAdminUserStats} from "../apis/Admin";
import {get_events} from "../apis/Event";
import * as LeagueAPI from "../apis/League";
import { generateResultsFromQualifiers, generateResultsFromQualifiersFourWide } from "../utilities/bracket_tools/ResultGeneration";
import { submitPicksAdminFourWideProMod, submitPicksAdminProMod } from "../apis/Pick";

export type AdminBracketEntryProps = {
    event: Event,
    brackets: Bracket[],
    picks: Pick[] | FourWidePick[],
    results: Result[] | FourWideResult[],
    original_results: Result[] | FourWideResult[],
    qualifiers: Qualifier[],
}

export default function AdminBracketEntryProMod() {
    // Admin check
    if (!JSON.parse(localStorage.getItem('user') as string).is_staff) {
        window.location.href = '/';
    }

    const [props, setProps] = useState<AdminBracketEntryProps | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [isMdOrUp, setIsMdOrUp] = useState(useMediaQuery((theme: Theme) => theme.breakpoints.up('md')));

    // Selection states
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
    const [isFourWide, setIsFourWide] = useState(false);

    // Data states
    const [users, setUsers] = useState<User[]>([]);
    const [leagues, setLeagues] = useState<League[]>([]);
    const [events, setEvents] = useState<Event[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);

    // UI states
    const [resetOpen, setResetOpen] = useState(false);
    const [open, setOpen] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [severity, setSeverity] = useState<"success" | "info" | "warning" | "error" | undefined>("error");

    const handleResetClickOpen = () => {
        setResetOpen(true);
    };
    const handleResetClose = () => {
        setResetOpen(false);
    };

    useEffect(() => {
        // Load initial data
        loadInitialData();
        
        const handleResize = () => {
            setIsMdOrUp(window.innerWidth >= 960);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        
        // Load users
        try {
            const userStatsResponse = await GetAdminUserStats();
            if (userStatsResponse.succeeded && userStatsResponse.data) {
                // Convert user list to User objects
                const userList = userStatsResponse.data.user_list.map((userData: any) => ({
                    id: userData.id,
                    nickname: userData.email || `User ${userData.id}`,
                    email: userData.email,
                    first_name: undefined,
                    last_name: undefined,
                    is_staff: false,
                    phone_number: undefined
                } as User));
                setUsers(userList);
            }
        } catch (e) {
            console.error("Failed to load users:", e);
        }

        // Load events
        try {
            const eventsResponse = await get_events();
            if (eventsResponse.succeeded && eventsResponse.data) {
                setEvents(eventsResponse.data);
            }
        } catch (e) {
            console.error("Failed to load events:", e);
        }

        // Load drivers
        try {
            const driversResponse = await get_drivers();
            if (driversResponse.succeeded && driversResponse.data) {
                let driversData: Driver[] = driversResponse.data;
                driversData.forEach((driver: any) => {
                    let newDivisions: RaceClass[] = [];
                    for (let division of driver.divisions) {
                        newDivisions.push(raceClassFromInt(division));
                    }
                    driver.divisions = newDivisions;
                });
                setDrivers(driversData);
            }
        } catch (e) {
            console.error("Failed to load drivers:", e);
        }

        setLoading(false);
    };

    const loadLeaguesForUser = async (userId: number) => {
        try {
            const response = await LeagueAPI.get_leagues_per_player(userId);
            if (response.succeeded && response.data) {
                setLeagues(response.data);
            }
        } catch (e) {
            console.error("Failed to load leagues for user:", e);
        }
    };

    const loadBracketData = async () => {
        if (!selectedUser || !selectedLeague || !selectedEvent) {
            setMessage("Please select a user, league, and event");
            setSeverity("error");
            setOpen(true);
            return;
        }

        setLoading(true);
        try {
            const response = await bracket_screen_load_pro_mod(
                selectedLeague.id.toString(),
                selectedUser.id.toString(),
                selectedEvent.id as string
            );

            if (response.succeeded) {
                let driversData: Driver[] = response.data.drivers;
                driversData.forEach((driver: any) => {
                    let newDivisions: RaceClass[] = [];
                    for (let division of driver.divisions) {
                        newDivisions.push(raceClassFromInt(division));
                    }
                    driver.divisions = newDivisions;
                });

                let event = response.data.event as Event;
                setIsFourWide(event.is_four_wide);

                response.data.brackets.forEach((bracket: any) => {
                    bracket.race_class = raceClassFromInt(bracket.race_class);
                });
                let brackets: Bracket[] = response.data.brackets;

                response.data.qualifiers.forEach((qualifier: any) => {
                    qualifier.event = event;
                    qualifier.bracket = response.data.brackets.find((bracket: Bracket) => {
                        return bracket.id === qualifier.bracket_id
                    });
                    qualifier.driver = driversData.find((driver: any) => {
                        return driver.id === qualifier.driver_id
                    });
                    qualifier.race_class = raceClassFromInt(qualifier.race_class)
                    delete qualifier.bracket_id;
                    delete qualifier.driver_id;
                    delete qualifier.event_id;
                });
                let qualifiers: Qualifier[] = response.data.qualifiers;

                if (event.is_four_wide) {
                    response.data.results.forEach((result: any) => {
                        result.bracket = brackets.find((bracket: Bracket) => {
                            return bracket.id === result.bracket_id
                        });
                        delete result.bracket_id;
                        result.first = driversData.find((driver: any) => {
                            return driver.id === result.first_id
                        });
                        delete result.first_id;
                        result.second = driversData.find((driver: any) => {
                            return driver.id === result.second_id
                        });
                        delete result.second_id;
                        result.third = driversData.find((driver: any) => {
                            return driver.id === result.third_id
                        });
                        delete result.third_id;
                        result.fourth = driversData.find((driver: any) => {
                            return driver.id === result.fourth_id
                        });
                        delete result.fourth_id;
                        result.competitor_1 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_1_id
                        });
                        delete result.competitor_1_id;
                        result.competitor_2 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_2_id
                        });
                        delete result.competitor_2_id;
                        result.competitor_3 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_3_id
                        });
                        delete result.competitor_3_id;
                        result.competitor_4 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_4_id
                        });
                        delete result.competitor_4_id;
                    });
                } else {
                    response.data.results.forEach((result: any) => {
                        result.bracket = brackets.find((bracket: Bracket) => {
                            return bracket.id === result.bracket_id
                        });
                        delete result.bracket_id;
                        result.winner = driversData.find((driver: any) => {
                            return driver.id === result.winner_id
                        });
                        delete result.winner_id;
                        result.loser = driversData.find((driver: any) => {
                            return driver.id === result.loser_id
                        });
                        delete result.loser_id;
                        result.competitor_1 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_1_id
                        });
                        delete result.competitor_1_id;
                        result.competitor_2 = driversData.find((driver: any) => {
                            return driver.id === result.competitor_2_id
                        });
                        delete result.competitor_2_id;
                    });
                }
                let staticResults: any[] = [];
                for (let i = 0; i < brackets.length; i++) {
                    let bracket = brackets[i];
                    let qualifiers2 = qualifiers.filter((qualifier) => qualifier.bracket === bracket);
                    let results = event.is_four_wide ? generateResultsFromQualifiersFourWide(qualifiers2, bracket) : generateResultsFromQualifiers(qualifiers2, bracket);
                    staticResults.push(...results);
                }
                let results2: Result[] | FourWideResult[] = staticResults;
                // Go through original results match them to the corresponding result in results2 and set the result_id to the game_code
                response.data.results.forEach((result: any) => {
                    console.log(result)
                    let result2 = (results2 as any[]).find((result2: any) => result2.round === result.round && result2.bracket.id === result.bracket.id && result2.game === result.game);
                    if (result2) {
                        result2.game_code = result.game_code;
                        let result3 = response.data.results.find((result3: any) => result3.round === result.round && result3.bracket.id === result.bracket.id && result3.game === result.game);
                        if (result3) {
                            result3.game_code = result.game_code;
                        }
                    }
                });

                // Generate blank picks for the selected user
                let picks: Pick[] | FourWidePick[] = generateBlankPicks({
                    results: response.data.results,
                    isFourWide: event.is_four_wide,
                    player: selectedUser,
                    league: selectedLeague,
                });

                setProps({
                    event: event,
                    picks: picks,
                    brackets: brackets,
                    results: response.data.results,
                    original_results: response.data.results,
                    qualifiers: qualifiers,
                });

                setMessage("Bracket data loaded successfully");
                setSeverity("success");
                setOpen(true);
            } else {
                setMessage("Failed to load bracket data");
                setSeverity("error");
                setOpen(true);
            }
        } catch (e) {
            console.error("Error loading bracket data:", e);
            setMessage("Failed to load bracket data");
            setSeverity("error");
            setOpen(true);
        }
        setLoading(false);
    };

    function updatePickTwoWide(pick: Pick) {
        let picks = props?.picks as Pick[];
        if (pick.pick_status === PickStatus.PENDING) {
            // Winner does not exist make sure no one from this pick is in any future picks
            if (pick.winner !== undefined) {
                let game_code = pick.result.game_code;
                let nextPick = picks.find((pick2: Pick) => {
                    return pick2.pick_status !== PickStatus.TIEBREAKER && (pick2.result.competitor_1_game_code === game_code || pick2.result.competitor_2_game_code === game_code);
                });
                if (nextPick !== undefined) {
                    if (nextPick.result.competitor_1_game_code === game_code) {
                        nextPick.competitor_1 = pick.winner;
                        nextPick.seed_1 = pick.winner_seed;
                    } else if (nextPick.result.competitor_2_game_code === game_code) {
                        nextPick.competitor_2 = pick.winner;
                        nextPick.seed_2 = pick.winner_seed;
                    }
                }
                let round = pick.result.round;
                let invalidPicks = picks.filter((pick2: Pick) => {
                    return pick2.result.round > round &&
                        (pick2.competitor_1 === pick.loser ||
                            pick2.competitor_2 === pick.loser
                        );
                });
                for (let invalidPick of invalidPicks) {
                    if (invalidPick.competitor_1 === pick.loser) {
                        invalidPick.competitor_1 = undefined;
                        invalidPick.seed_1 = undefined;
                    } else if (invalidPick.competitor_2 === pick.loser) {
                        invalidPick.competitor_2 = undefined;
                        invalidPick.seed_2 = undefined;
                    }
                }
                for (let invalidPick of invalidPicks) {
                    picks.forEach((pick2: Pick) => {
                        if (pick2.id === invalidPick.id) {
                            pick2 = invalidPick;
                        }
                    })
                }
                picks.forEach((pick2: Pick) => {
                    if (pick2.id === nextPick?.id) {
                        pick2 = nextPick;
                    } else if (pick2.id === pick.id) {
                        pick2 = pick;
                    }
                })
            }
            // Winner exists make sure loser from this pick is not in any future picks
            else {
                let round = pick.result.round;
                let invalidPicks = picks.filter((pick2: Pick) => {
                    return pick2.result.round > round &&
                        (pick2.competitor_1 === pick.competitor_1 ||
                            pick2.competitor_1 === pick.competitor_2 ||
                            pick2.competitor_2 === pick.competitor_1 ||
                            pick2.competitor_2 === pick.competitor_2
                        );
                });
                for (let invalidPick of invalidPicks) {
                    if (invalidPick.competitor_1 === pick.competitor_1 || invalidPick.competitor_1 === pick.competitor_2) {
                        if (invalidPick.competitor_1 === invalidPick.winner) {
                            invalidPick.winner = undefined;
                            invalidPick.winner_seed = undefined;
                            invalidPick.loser = undefined;
                            invalidPick.loser_seed = undefined;
                        }
                        invalidPick.competitor_1 = undefined;
                        invalidPick.seed_1 = undefined;
                    }
                    if (invalidPick.competitor_2 === pick.competitor_1 || invalidPick.competitor_2 === pick.competitor_2) {
                        if (invalidPick.competitor_2 === invalidPick.winner) {
                            invalidPick.winner = undefined;
                            invalidPick.winner_seed = undefined;
                            invalidPick.loser = undefined;
                            invalidPick.loser_seed = undefined;
                        }
                        invalidPick.competitor_2 = undefined;
                        invalidPick.seed_2 = undefined;
                    }
                }
                for (let invalidPick of invalidPicks) {
                    picks.forEach((pick2: Pick) => {
                        if (pick2.id === invalidPick.id) {
                            pick2 = invalidPick;
                        }
                    })
                }
                picks.forEach((pick2: Pick) => {
                    if (pick2.id === pick.id) {
                        pick2 = pick;
                    }
                })
            }
        } else if (pick.pick_status === PickStatus.TIEBREAKER) {
            picks.forEach((pick2: Pick) => {
                if (pick2.id === pick.id) {
                    pick2 = pick;
                }
            })
        } else {
            return;
        }
        // set props
        if (props !== undefined) {
            setProps({
                event: props.event,
                picks: picks,
                brackets: props.brackets,
                results: props.results,
                original_results: props.original_results,
                qualifiers: props.qualifiers,
            });
        }
    }

    function updatePickFourWide(pick: FourWidePick, picks: FourWidePick[]) {
        if (pick.pick_status === PickStatus.PENDING) {
            if (pick.first) {
                picks.forEach((pick2: FourWidePick) => {
                    if(pick2.pick_status !== PickStatus.TIEBREAKER) {
                        if (pick2.result.competitor_1_game_code === pick.result.game_code) {
                            if (pick2.first && pick2.first === pick2.competitor_1) {
                                pick2.first = pick.first;
                            } else if (pick2.second && pick2.second === pick2.competitor_1) {
                                pick2.second = pick.first;
                            } else if (pick2.third && pick2.third === pick2.competitor_1) {
                                pick2.third = pick.first;
                            } else if (pick2.fourth && pick2.fourth === pick2.competitor_1) {
                                pick2.fourth = pick.first;
                            }
                            pick2.competitor_1 = pick.first;
                        } else if (pick2.result.competitor_3_game_code === pick.result.game_code) {
                            if (pick2.first && pick2.first === pick2.competitor_3) {
                                pick2.first = pick.first;
                            } else if (pick2.second && pick2.second === pick2.competitor_3) {
                                pick2.second = pick.first;
                            } else if (pick2.third && pick2.third === pick2.competitor_3) {
                                pick2.third = pick.first;
                            } else if (pick2.fourth && pick2.fourth === pick2.competitor_3) {
                                pick2.fourth = pick.first;
                            }
                            pick2.competitor_3 = pick.first;
                        }
                        if (pick2.result.competitor_2_game_code === pick.result.game_code && pick2.competitor_2 === pick.first) {
                            pick2.competitor_2 = undefined;
                            pick2.seed_2 = undefined;
                        } else if (pick2.result.competitor_4_game_code === pick.result.game_code && pick2.competitor_4 === pick.first) {
                            pick2.competitor_4 = undefined;
                            pick2.seed_4 = undefined;
                        }
                    }
                });
            }
            // If first place is not present validate no competitor is passed this race
            else {
                let drivers_to_remove = [
                    pick.competitor_1,
                    pick.competitor_2,
                    pick.competitor_3,
                    pick.competitor_4
                ].filter((driver: Driver | undefined) => driver !== undefined);
                fourWideRemoveDrivers(picks, pick, drivers_to_remove);
            }
            // Make sure second goes only into second or fourth competitor_slot_of next_pick
            if (pick.second) {
                picks.forEach((pick2: FourWidePick) => {
                    if(pick2.pick_status !== PickStatus.TIEBREAKER) {
                        if (pick2.result.competitor_2_game_code === pick.result.game_code) {
                            pick2.competitor_2 = pick.second;
                        } else if (pick2.result.competitor_4_game_code === pick.result.game_code) {
                            pick2.competitor_4 = pick.second;
                        }
                        if (pick2.result.competitor_1_game_code === pick.result.game_code && pick2.competitor_1 === pick.second) {
                            pick2.competitor_1 = undefined;
                            pick2.seed_1 = undefined;
                        } else if (pick2.result.competitor_3_game_code === pick.result.game_code && pick2.competitor_3 === pick.second) {
                            pick2.competitor_3 = undefined;
                            pick2.seed_3 = undefined;
                        }
                    }
                });
            }
            // If second place is not present validate no competitor is passed (except for first)
            else {
                if (pick.first) {
                    let drivers_to_remove = [
                        pick.competitor_1,
                        pick.competitor_2,
                        pick.competitor_3,
                        pick.competitor_4
                    ].filter((driver: Driver | undefined) => driver !== pick.first);
                    fourWideRemoveDrivers(picks, pick, drivers_to_remove);
                }
            }
            // Make sure third place and fourth place finishes are not in any future picks
            picks.forEach((pick2: FourWidePick) => {
                picks.forEach((pick3: FourWidePick) => {
                    if (pick3.result.round > pick2.result.round) {
                        if (pick2.third === pick3.competitor_1) {
                            pick3.competitor_1 = undefined;
                            pick3.seed_1 = undefined;
                        } else if (pick2.third === pick3.competitor_2) {
                            pick3.competitor_2 = undefined;
                            pick3.seed_2 = undefined;
                        } else if (pick2.third === pick3.competitor_3) {
                            pick3.competitor_3 = undefined;
                            pick3.seed_3 = undefined;
                        } else if (pick2.third === pick3.competitor_4) {
                            pick3.competitor_4 = undefined;
                            pick3.seed_4 = undefined;
                        }
                        if (pick2.third === pick3.first) {
                            pick3.first = undefined;
                            pick3.first_seed = undefined;
                        } else if (pick2.third === pick3.second) {
                            pick3.second = undefined;
                            pick3.second_seed = undefined;
                        } else if (pick2.third === pick3.third) {
                            pick3.third = undefined;
                            pick3.third_seed = undefined;
                        } else if (pick2.third === pick3.fourth) {
                            pick3.fourth = undefined;
                            pick3.fourth_seed = undefined;
                        }
                        if (pick2.fourth === pick3.competitor_1) {
                            pick3.competitor_1 = undefined;
                            pick3.seed_1 = undefined;
                        } else if (pick2.fourth === pick3.competitor_2) {
                            pick3.competitor_2 = undefined;
                            pick3.seed_2 = undefined;
                        } else if (pick2.fourth === pick3.competitor_3) {
                            pick3.competitor_3 = undefined;
                            pick3.seed_3 = undefined;
                        } else if (pick2.fourth === pick3.competitor_4) {
                            pick3.competitor_4 = undefined;
                            pick3.seed_4 = undefined;
                        }
                        if (pick2.fourth === pick3.first) {
                            pick3.first = undefined;
                            pick3.first_seed = undefined;
                        } else if (pick2.fourth === pick3.second) {
                            pick3.second = undefined;
                            pick3.second_seed = undefined;
                        } else if (pick2.fourth === pick3.third) {
                            pick3.third = undefined;
                            pick3.third_seed = undefined;
                        } else if (pick2.fourth === pick3.fourth) {
                            pick3.fourth = undefined;
                            pick3.fourth_seed = undefined;
                        }
                    }
                });
            });
        } else if (pick.pick_status === PickStatus.TIEBREAKER) {
            picks.forEach((pick2: Pick) => {
                if (pick2.id === pick.id) {
                    pick2 = pick;
                }
            })
        } else {
            return;
        }
        let startingRound = pick.result.round;
        let round = pick.result.round + 1;
        let currentPick = pick;
        let nextPick: FourWidePick | undefined = undefined;
        while (round < 4) {
            picks.forEach((pick2: FourWidePick) => {
                if (pick2.result.round === round && pick2.pick_status !== PickStatus.TIEBREAKER) {
                    if ([pick2.result.competitor_1_game_code,
                        pick2.result.competitor_2_game_code,
                        pick2.result.competitor_3_game_code,
                        pick2.result.competitor_4_game_code].includes(currentPick.result.game_code)) {

                        pick2.first = undefined;
                        pick2.second = undefined;
                        pick2.third = undefined;
                        pick2.fourth = undefined;
                        pick2.first_seed = undefined;
                        pick2.second_seed = undefined;
                        pick2.third_seed = undefined;
                        pick2.fourth_seed = undefined;
                        nextPick = pick2;
                        if (round > startingRound + 1) {
                            if (pick2.result.competitor_1_game_code === currentPick.result.game_code) {
                                pick2.competitor_1 = undefined;
                                pick2.seed_1 = undefined;
                                pick2.competitor_2 = undefined;
                                pick2.seed_2 = undefined;
                            } else if (pick2.result.competitor_3_game_code === currentPick.result.game_code) {
                                pick2.competitor_3 = undefined;
                                pick2.seed_3 = undefined;
                                pick2.competitor_4 = undefined;
                                pick2.seed_4 = undefined;
                            }
                        }
                    }
                }
            });
            if (nextPick !== undefined) {
                currentPick = nextPick;
                round++;
            } else {
                break;
            }
        }
        picks.forEach((pick2: FourWidePick) => {
            if(pick2.competitor_1){
                pick2.seed_1 = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_1})?.seed;
            }
            else{
                pick2.seed_1 = undefined;
            }
            if(pick2.competitor_2){
                pick2.seed_2 = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_2})?.seed;
            }
            else{
                pick2.seed_2 = undefined;
            }
            if(pick2.competitor_3){
                pick2.seed_3 = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_3})?.seed;
            }
            else{
                pick2.seed_3 = undefined;
            }
            if(pick2.competitor_4){
                pick2.seed_4 = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_4})?.seed;
            }
            else{
                pick2.seed_4 = undefined;
            }
            if(pick2.first){
                pick2.first_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.first})?.seed;
            }
            else{
                pick2.first_seed = undefined;
            }
            if(pick2.second){
                pick2.second_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.second})?.seed;
            }
            else{
                pick2.second_seed = undefined;
            }
            if(pick2.third){
                pick2.third_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.third})?.seed;
            }
            else{
                pick2.third_seed = undefined;
            }
            if(pick2.fourth){
                pick2.fourth_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.fourth})?.seed;
            }
            else{
                pick2.fourth_seed = undefined;
            }
        });
        if (props !== undefined) {
            setProps({
                event: props.event,
                picks: picks,
                brackets: props.brackets,
                results: props.results,
                original_results: props.original_results,
                qualifiers: props.qualifiers,
            });
        }
    }

    function fourWideRemoveDrivers(picks: FourWidePick[], pick: FourWidePick, drivers_to_remove: (Driver | undefined)[]) {
        picks.forEach((pick2: FourWidePick) => {
            if(pick2.pick_status !== PickStatus.TIEBREAKER) {
                if (pick2.result.round > pick.result.round) {
                    if (drivers_to_remove.includes(pick2.competitor_1)) {
                        pick2.competitor_1 = undefined;
                        pick2.seed_1 = undefined;
                    } else if (drivers_to_remove.includes(pick2.competitor_2)) {
                        pick2.competitor_2 = undefined;
                        pick2.seed_2 = undefined;
                    } else if (drivers_to_remove.includes(pick2.competitor_3)) {
                        pick2.competitor_3 = undefined;
                        pick2.seed_3 = undefined;
                    } else if (drivers_to_remove.includes(pick2.competitor_4)) {
                        pick2.competitor_4 = undefined;
                        pick2.seed_4 = undefined;
                    }
                    if (drivers_to_remove.includes(pick2.first)) {
                        pick2.first = undefined;
                        pick2.first_seed = undefined;
                    } else if (drivers_to_remove.includes(pick2.second)) {
                        pick2.second = undefined;
                        pick2.second_seed = undefined;
                    } else if (drivers_to_remove.includes(pick2.third)) {
                        pick2.third = undefined;
                        pick2.third_seed = undefined;
                    } else if (drivers_to_remove.includes(pick2.fourth)) {
                        pick2.fourth = undefined;
                        pick2.fourth_seed = undefined;
                    }
                }
            }
        });
    }

    function updatePick(pick: Pick | FourWidePick) {
        if (props?.event.is_four_wide) {
            updatePickFourWide(pick as FourWidePick, props?.picks as FourWidePick[]);
        } else {
            updatePickTwoWide(pick as Pick);
        }
    }

    function cleanFourWidePicksForSave() {
        let picksCopy: any = (props?.picks as FourWidePick[]).map((pick: FourWidePick) => ({...pick}));
        picksCopy.forEach((pick: any) => {
            pick.pick_status = pick.pick_status + 1;
            pick.possible_points = calculatePossiblePointsFourWide(pick);
            pick.result_id = pick.result.game_code;
            delete pick.result;
            pick.league_id = selectedLeague?.id;
            delete pick.league
            pick.competitor_1_id = pick.competitor_1?.id ?? null;
            delete pick.competitor_1;
            pick.competitor_2_id = pick.competitor_2?.id ?? null;
            delete pick.competitor_2;
            pick.competitor_3_id = pick.competitor_3?.id ?? null;
            delete pick.competitor_3;
            pick.competitor_4_id = pick.competitor_4?.id ?? null;
            delete pick.competitor_4;
            pick.first_id = pick.first?.id ?? null;
            delete pick.first;
            pick.second_id = pick.second?.id ?? null;
            delete pick.second;
            pick.third_id = pick.third?.id ?? null;
            delete pick.third;
            pick.fourth_id = pick.fourth?.id ?? null;
            delete pick.fourth;
            pick.first_seed = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.first_id})?.seed ?? null;
            pick.second_seed = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.second_id})?.seed ?? null;
            pick.third_seed = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.third_id})?.seed ?? null;
            pick.fourth_seed = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.fourth_id})?.seed ?? null;
            pick.seed_1 = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.competitor_1_id})?.seed ?? null;
            pick.seed_2 = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.competitor_2_id})?.seed ?? null;
            pick.seed_3 = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.competitor_3_id})?.seed ?? null;
            pick.seed_4 = props?.qualifiers.find((qualifier: Qualifier) => {return qualifier.driver !== undefined && qualifier.driver.id === pick.competitor_4_id})?.seed ?? null;
            pick.tiebreaker = pick.tiebreaker ?? null;
            pick.player_id = selectedUser?.id;
            delete pick.player;
            if (![pick.first_id, pick.second_id, pick.third_id].includes(null) && pick.fourth_id === null) {
                if (![pick.first_id, pick.second_id, pick.third_id].includes(pick.competitor_1_id) && pick.competitor_1_id !== null) {
                    pick.fourth_id = pick.competitor_1_id;
                } else if (![pick.first_id, pick.second_id, pick.third_id].includes(pick.competitor_2_id) && pick.competitor_2_id !== null) {
                    pick.fourth_id = pick.competitor_2_id;
                } else if (![pick.first_id, pick.second_id, pick.third_id].includes(pick.competitor_3_id) && pick.competitor_3_id !== null) {
                    pick.fourth_id = pick.competitor_3_id;
                } else if (![pick.first_id, pick.second_id, pick.third_id].includes(pick.competitor_4_id) && pick.competitor_4_id !== null) {
                    pick.fourth_id = pick.competitor_4_id;
                }
            }
        });
        return picksCopy;
    }

    function calculatePossiblePointsFourWide(pick: any) {
        let possible_points = 0;
        if(pick.pick_status !== 4) {
            if (pick.first && pick.second && pick.third && pick.fourth) {
                switch(pick.result.round) {
                    case 1:
                        possible_points = 5;
                        break;
                    case 2:
                        possible_points = 9;
                        break;
                    case 3:
                        possible_points = 17;
                        break;
                }
            }
        }
        return possible_points;
    }

    function cleanPicksForSave() {
        let picksCopy: any = (props?.picks as Pick[]).map((pick: Pick) => ({...pick}));
        picksCopy.forEach((pick: any) => {
            pick.pick_status = pick.pick_status + 1;
            pick.possible_points = calculatePossiblePointsTwoWide(pick);
            pick.result_id = pick.result.game_code;
            delete pick.result;
            pick.league_id = selectedLeague?.id;
            delete pick.league
            pick.competitor_1_id = pick.competitor_1?.id ?? null;
            delete pick.competitor_1;
            pick.competitor_2_id = pick.competitor_2?.id ?? null;
            delete pick.competitor_2;
            pick.winner_id = pick.winner?.id ?? null;
            delete pick.winner;
            pick.winner_seed = pick.winner_seed ?? null;
            pick.loser_seed = pick.loser_seed ?? null;
            pick.seed_1 = pick.seed_1 ?? null;
            pick.seed_2 = pick.seed_2 ?? null;
            pick.tiebreaker = pick.tiebreaker ?? null;
            pick.loser_id = pick.loser?.id ?? null;
            delete pick.loser;
            pick.player_id = selectedUser?.id;
            delete pick.player;
            if (pick.winner_id !== null && pick.loser_id === null) {
                if (pick.competitor_1_id !== pick.winner_id) {
                    pick.loser_id = pick.competitor_1_id;
                } else {
                    pick.loser_id = pick.competitor_2_id;
                }
            }
        });
        return picksCopy;
    }

    function calculatePossiblePointsTwoWide(pick: any) {
        let possible_points = 0;
        if(pick.pick_status !== 4) {
            if (pick.seed_1 && pick.seed_2 && pick.winner_seed) {
                possible_points = Math.pow(2, pick.result.round - 1);
                if ((pick.seed_1 - pick.seed_2 >= 7 && pick.seed_1 === pick.winner_seed) ||
                    (pick.seed_2 - pick.seed_1 >= 7 && pick.seed_2 === pick.winner_seed)) {
                    possible_points *= 2;
                }
            }
            possible_points *= props?.brackets[0].points_modifier ?? 1;
        }
        return possible_points;
    }

    async function handleSubmitPicks() {
        if (!selectedUser || !selectedLeague || !props) {
            setMessage("Please select a user, league, and load bracket data");
            setSeverity("error");
            setOpen(true);
            return;
        }

        let picksCopy;
        if (props.event.is_four_wide) {
            picksCopy = cleanFourWidePicksForSave();
        } else {
            picksCopy = cleanPicksForSave();
        }

        // Console out the picks for now
        console.log("Submitting picks:", picksCopy);

        // Comment out the actual submit call for now
        
        if (props.event.is_four_wide) {
            const response = await submitPicksAdminFourWideProMod(
                localStorage.getItem('access_token') ?? "", 
                localStorage.getItem('refresh_token') ?? "", 
                picksCopy
            );
            if (response.succeeded) {
                setMessage("Picks submitted successfully!");
                setSeverity("success");
                setOpen(true);
            } else {
                setMessage(`Failed to submit picks: ${response.message}`);
                setSeverity("error");
                setOpen(true);
            }
        } else {
            const response = await submitPicksAdminProMod(
                localStorage.getItem('access_token') ?? "", 
                localStorage.getItem('refresh_token') ?? "", 
                picksCopy
            );
            if (response.succeeded) {
                setMessage("Picks submitted successfully!");
                setSeverity("success");
                setOpen(true);
            } else {
                setMessage(`Failed to submit picks: ${response.message}`);
                setSeverity("error");
                setOpen(true);
            }
        }
        
    }

    return (
        <>
            <Helmet>
                <title>DRBB | Admin Bracket Entry Pro Mod</title>
            </Helmet>
            <Layout>
                <CssBaseline/>
                <Snackbar anchorOrigin={{vertical: "top", horizontal: "center"}} open={open} onClose={() => {
                    setOpen(false)
                }}
                          autoHideDuration={6000}>
                    <Alert elevation={6} severity={severity} sx={{width: '100%'}} variant="filled">
                        {message}
                    </Alert>
                </Snackbar>
                <Dialog
                    open={resetOpen}
                    onClose={handleResetClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Reset Picks"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to reset the picks?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleResetClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={() => {
                            if (props?.event && selectedUser && selectedLeague) {
                                let staticResults: any[] = [];
                                for (let i = 0; i < props.brackets.length; i++) {
                                    let bracket = props.brackets[i];
                                    let qualifiers = props.qualifiers.filter((qualifier) => qualifier.bracket === bracket);
                                    let results = props.event.is_four_wide ? generateResultsFromQualifiersFourWide(qualifiers, bracket) : generateResultsFromQualifiers(qualifiers, bracket);
                                    staticResults.push(...results);
                                }
                                props.original_results.forEach((result: any) => {
                                    let result2 = (staticResults as any[]).find((result2: any) => result2.round === result.round && result2.bracket_id === result.bracket_id && result2.game_code === result.game_code);
                                    if (result2) {
                                        result2.game_code = result.game_code;
                                    }
                                });
                                setProps({
                                    event: props.event,
                                    picks: generateBlankPicks({
                                        isFourWide: props.event.is_four_wide,
                                        player: selectedUser,
                                        league: selectedLeague,
                                        results: staticResults,
                                    }),
                                    brackets: props.brackets,
                                    results: staticResults,
                                    original_results: props.original_results,
                                    qualifiers: props.qualifiers,
                                });
                            }
                            handleResetClose();
                        }} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                <Grid container sx={{
                    display: 'flex',
                    flexDirection: "column",
                    height: '100%',
                    maxWidth: '100vw',
                }}>
                    <Typography variant={"h4"} align={"center"} sx={{mt: 2, mb: 2}}>
                        Admin Bracket Entry
                    </Typography>
                    
                    {/* Selection Controls */}
                    <Grid container spacing={2} sx={{p: 2, backgroundColor: 'background.paper', mb: 2}}>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={users.sort((a, b) => a.id - b.id)}
                                getOptionLabel={(option) => `(ID: ${option.id})`}
                                value={selectedUser}
                                onChange={(event, newValue) => {
                                    setSelectedUser(newValue);
                                    setSelectedLeague(null);
                                    setLeagues([]);
                                    if (newValue) {
                                        loadLeaguesForUser(newValue.id);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select User" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={leagues}
                                getOptionLabel={(option) => option.name}
                                value={selectedLeague}
                                onChange={(event, newValue) => {
                                    setSelectedLeague(newValue);
                                }}
                                disabled={!selectedUser}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select League" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={events}
                                getOptionLabel={(option) => option.name}
                                value={selectedEvent}
                                onChange={(event, newValue) => {
                                    setSelectedEvent(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Event" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                onClick={loadBracketData}
                                disabled={!selectedUser || !selectedLeague || !selectedEvent}
                                sx={{mt: 1}}
                            >
                                Load Bracket
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Action Buttons */}
                    {props?.picks !== undefined && props.picks.length > 0 &&
                        <Toolbar
                            component="nav"
                            variant="dense"
                            sx={{
                                justifyContent: 'space-between',
                                overflowX: 'auto',
                                maxWidth: '100vw',
                            }}
                        >
                            <Button
                                variant="contained"
                                endIcon={<PublishIcon/>}
                                sx={{
                                    minWidth: '120px',
                                    backgroundColor: "#486ce7",
                                    color: "#ffffff",
                                    marginX: 1,
                                    ":hover": {
                                        backgroundColor: "#c40000",
                                        color: "#ffffff",
                                    }
                                }}
                                onClick={handleSubmitPicks}
                            >
                                <Typography variant={"h6"} fontWeight={"bold"}>
                                    Submit
                                </Typography>
                            </Button>
                            <Button
                                variant="contained"
                                endIcon={<RestartAltIcon/>}
                                sx={{
                                    minWidth: '120px',
                                    backgroundColor: "#486ce7",
                                    color: "#ffffff",
                                    marginX: 1,
                                    ":hover": {
                                        backgroundColor: "#c40000",
                                        color: "#ffffff",
                                    }
                                }}
                                onClick={() => {
                                    handleResetClickOpen()
                                }}
                            >
                                <Typography variant={"h6"} fontWeight={"bold"}>
                                    Reset
                                </Typography>
                            </Button>
                        </Toolbar>
                    }

                    {/* Bracket Display */}
                    {props?.picks !== undefined && props.picks.length > 0 ?
                        props?.event.is_four_wide ?
                            <Box sx={{
                                overflowX: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}>
                                <PickBracketContainerFourWideProMod
                                    picks_locked={false}
                                    brackets={props?.brackets}
                                    picks={props?.picks}
                                    results={props?.results}
                                    updatePick={updatePick}
                                    event={props?.event}
                                />
                            </Box>
                            :
                            <Box sx={{
                                overflowX: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}>
                                <PickBracketContainerProMod
                                    picks_locked={false}
                                    brackets={props?.brackets}
                                    picks={props?.picks}
                                    results={props?.results}
                                    updatePick={updatePick}
                                    event={props?.event}
                                />
                            </Box>
                        :
                        <Grid container sx={{height: '50vh', width: '100%'}}>
                            {loading ? <Loader/> : <Typography variant={"h6"} align={"center"} margin={"auto"}>Please select a user, league, and event to load bracket data.</Typography>}
                        </Grid>
                    }

                    
                </Grid>
            </Layout>
        </>
    );
} 